import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ShoppingCart from '../../components/ui/ShoppingCart';
import ContactHub from '../../components/ui/ContactHub';
import DashboardHeader from './components/DashboardHeader';
import UpcomingAppointments from './components/UpcomingAppointments';
import ServiceHistory from './components/ServiceHistory';
import RecurringServices from './components/RecurringServices';
import AccountInformation from './components/AccountInformation';
import LoyaltyProgram from './components/LoyaltyProgram';
import QuickActions from './components/QuickActions';

const CustomerPortal = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mock user data
  const mockUser = {
    id: 1,
    name: "Sarah Johnson",
    email: "sarah.johnson@email.com",
    phone: "(555) 123-4567",
    address: "123 Oak Street",
    city: "Springfield",
    state: "IL",
    zipCode: "62701",
    memberSince: "2023-03-15",
    preferences: {
      emailNotifications: true,
      smsNotifications: false,
      pushNotifications: true,
      marketingEmails: false,
      serviceReminders: true,
      promotionalOffers: true
    }
  };

  // Mock appointments data
  const mockAppointments = [
    {
      id: 1,
      service: "Deep House Cleaning",
      date: "2025-01-18",
      time: "10:00",
      location: "123 Oak Street, Springfield, IL",
      price: 149.99,
      duration: "3 hours",
      status: "confirmed",
      technician: {
        name: "Mike Rodriguez",
        avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
        rating: 4.9,
        experience: "5 years experience"
      }
    },
    {
      id: 2,
      service: "Carpet Cleaning",
      date: "2025-01-25",
      time: "14:00",
      location: "123 Oak Street, Springfield, IL",
      price: 89.99,
      duration: "2 hours",
      status: "pending",
      technician: {
        name: "Lisa Chen",
        avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
        rating: 4.8,
        experience: "3 years experience"
      }
    }
  ];

  // Mock service history data
  const mockServiceHistory = [
    {
      id: 1,
      name: "Office Deep Cleaning",
      date: "2024-12-15",
      location: "456 Business Ave, Springfield, IL",
      status: "completed",
      amount: 199.99,
      technician: {
        name: "John Smith",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
      },
      photos: true
    },
    {
      id: 2,
      name: "Residential Cleaning",
      date: "2024-11-28",
      location: "123 Oak Street, Springfield, IL",
      status: "completed",
      amount: 129.99,
      technician: {
        name: "Maria Garcia",
        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face"
      },
      photos: true
    },
    {
      id: 3,
      name: "Window Cleaning",
      date: "2024-11-10",
      location: "123 Oak Street, Springfield, IL",
      status: "cancelled",
      amount: 79.99,
      technician: {
        name: "David Wilson",
        avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face"
      },
      photos: false
    }
  ];

  // Mock recurring services data
  const mockRecurringServices = [
    {
      id: 1,
      service: "Weekly House Cleaning",
      location: "123 Oak Street, Springfield, IL",
      price: 89.99,
      frequency: "weekly",
      nextService: "2025-01-20",
      status: "active",
      completedServices: 12,
      autoRenewal: true,
      paymentMethod: {
        type: "Visa",
        last4: "4242"
      }
    },
    {
      id: 2,
      service: "Monthly Deep Cleaning",
      location: "123 Oak Street, Springfield, IL",
      price: 199.99,
      frequency: "monthly",
      nextService: "2025-02-01",
      status: "paused",
      completedServices: 3,
      autoRenewal: false,
      paymentMethod: {
        type: "Mastercard",
        last4: "8888"
      }
    }
  ];

  // Mock loyalty program data
  const mockLoyaltyData = {
    currentPoints: 2450,
    currentTier: "silver",
    nextTier: "gold",
    nextTierPoints: 5000,
    referralCode: "SARAH2024",
    referralReward: 500,
    referralStats: {
      totalReferrals: 3,
      pointsEarned: 1500
    },
    availableRewards: [
      {
        id: 1,
        title: "$25 Service Credit",
        description: "Apply to any cleaning service",
        pointsCost: 2500,
        expiryDate: "2025-06-30"
      },
      {
        id: 2,
        title: "Free Carpet Cleaning",
        description: "Up to 3 rooms",
        pointsCost: 3000,
        expiryDate: "2025-12-31"
      }
    ],
    recentActivity: [
      {
        id: 1,
        type: "earned",
        description: "Service completion bonus",
        points: 200,
        date: "2024-12-15"
      },
      {
        id: 2,
        type: "redeemed",
        description: "Redeemed $10 credit",
        points: 1000,
        date: "2024-11-28"
      },
      {
        id: 3,
        type: "earned",
        description: "Referral bonus",
        points: 500,
        date: "2024-11-15"
      }
    ]
  };

  useEffect(() => {
    // Check if user is authenticated
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      setUser({ ...mockUser, ...parsedUser });
    } catch (error) {
      console.error('Error parsing user data:', error);
      navigate('/login');
      return;
    }

    setLoading(false);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/homepage');
  };

  const handleEmergencyBooking = (serviceId) => {
    console.log('Emergency booking requested for service:', serviceId);
    // Implement emergency booking logic
  };

  const handleRegularBooking = () => {
    navigate('/service-details');
  };

  const handleContactSupport = () => {
    console.log('Contact support clicked');
    // This will be handled by the ContactHub component
  };

  const handleRescheduleAppointment = (appointmentId) => {
    console.log('Reschedule appointment:', appointmentId);
    // Implement reschedule logic
  };

  const handleCancelAppointment = (appointmentId) => {
    console.log('Cancel appointment:', appointmentId);
    // Implement cancel logic
  };

  const handleRebookService = (serviceId) => {
    console.log('Rebook service:', serviceId);
    navigate('/service-details');
  };

  const handleViewPhotos = (serviceId) => {
    console.log('View photos for service:', serviceId);
    // Implement photo viewing logic
  };

  const handleDownloadInvoice = (serviceId) => {
    console.log('Download invoice for service:', serviceId);
    // Implement invoice download logic
  };

  const handleUpdateSubscription = (subscriptionId, updates) => {
    console.log('Update subscription:', subscriptionId, updates);
    // Implement subscription update logic
  };

  const handleCancelSubscription = (subscriptionId) => {
    console.log('Cancel subscription:', subscriptionId);
    // Implement subscription cancellation logic
  };

  const handleUpdateProfile = async (profileData) => {
    console.log('Update profile:', profileData);
    // Implement profile update logic
    setUser(prev => ({ ...prev, ...profileData }));
  };

  const handleUpdatePreferences = async (preferences) => {
    console.log('Update preferences:', preferences);
    // Implement preferences update logic
    setUser(prev => ({ ...prev, preferences }));
  };

  const handleRedeemReward = (rewardId) => {
    console.log('Redeem reward:', rewardId);
    // Implement reward redemption logic
  };

  const handleReferFriend = (platform) => {
    console.log('Refer friend via:', platform);
    // Implement referral logic
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ShoppingCart />
      <ContactHub />
      
      <DashboardHeader 
        user={user}
        onEmergencyBooking={() => handleEmergencyBooking('general')}
        onLogout={handleLogout}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            <UpcomingAppointments
              appointments={mockAppointments}
              onReschedule={handleRescheduleAppointment}
              onCancel={handleCancelAppointment}
            />

            <ServiceHistory
              services={mockServiceHistory}
              onRebook={handleRebookService}
              onViewPhotos={handleViewPhotos}
              onDownloadInvoice={handleDownloadInvoice}
            />

            <RecurringServices
              subscriptions={mockRecurringServices}
              onUpdateSubscription={handleUpdateSubscription}
              onCancelSubscription={handleCancelSubscription}
            />

            <AccountInformation
              user={user}
              onUpdateProfile={handleUpdateProfile}
              onUpdatePreferences={handleUpdatePreferences}
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            <QuickActions
              onEmergencyBooking={handleEmergencyBooking}
              onRegularBooking={handleRegularBooking}
              onContactSupport={handleContactSupport}
            />

            <LoyaltyProgram
              loyaltyData={mockLoyaltyData}
              onRedeemReward={handleRedeemReward}
              onReferFriend={handleReferFriend}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default CustomerPortal;